"use client";

import { useState } from "react";
import { LuSlidersHorizontal } from "react-icons/lu";
import type { MentorFilters } from "@/hooks/useMentorFilters";

const COUNTRY_LABEL: Record<string, string> = { "United States": "USA" };
const countryLabel = (c: string) => COUNTRY_LABEL[c] ?? c;

const LIMIT = 5;

function ShowMoreList({
  items,
  selected,
  onToggle,
  emptyText,
  display,
}: {
  items: string[];
  selected: string[];
  onToggle: (v: string) => void;
  emptyText: string;
  display?: (v: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, LIMIT);
  const overflow = items.length - LIMIT;

  if (items.length === 0) {
    return <p className="text-xs text-gray-400">{emptyText}</p>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {visible.map((v) => (
        <label
          key={v}
          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
        >
          <input
            type="checkbox"
            checked={selected.includes(v)}
            onChange={() => onToggle(v)}
            className="rounded border-gray-300 dark:border-white/20 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="truncate">{display ? display(v) : v}</span>
        </label>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-0.5 text-left text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {expanded ? "Show less" : `+${overflow} more`}
        </button>
      )}
    </div>
  );
}

export type MentorFilterPanelProps = {
  countries: string[];
  fields: string[];
  maxRate: number;
  filters: MentorFilters;
  setFilters: (
    update:
      | Partial<MentorFilters>
      | ((prev: MentorFilters) => MentorFilters)
  ) => void;
  clear: () => void;
  activeCount: number;
};

export function MentorFilterPanel({
  countries,
  fields,
  maxRate,
  filters,
  setFilters,
  clear,
  activeCount,
}: MentorFilterPanelProps) {
  const toggleCountry = (c: string) =>
    setFilters((prev) => ({
      ...prev,
      countries: prev.countries.includes(c)
        ? prev.countries.filter((x) => x !== c)
        : [...prev.countries, c],
    }));

  const toggleField = (f: string) =>
    setFilters((prev) => ({
      ...prev,
      fields: prev.fields.includes(f)
        ? prev.fields.filter((x) => x !== f)
        : [...prev.fields, f],
    }));

  const currentMaxRate = filters.maxHourlyRate ?? maxRate;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LuSlidersHorizontal className="w-4 h-4 text-gray-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm uppercase tracking-wide">
            Filters
          </h3>
        </div>
        {activeCount > 0 && (
          <button
            onClick={clear}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Countries */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Country of expertise
        </h4>
        <ShowMoreList
          items={countries}
          selected={filters.countries}
          onToggle={toggleCountry}
          emptyText="No countries in dataset"
          display={countryLabel}
        />
      </div>

      {/* Fields */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Field of study
        </h4>
        <ShowMoreList
          items={fields}
          selected={filters.fields}
          onToggle={toggleField}
          emptyText="No fields in dataset"
        />
      </div>

      {/* Rating */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Minimum rating
          </h4>
          <span className="text-xs text-gray-500">{filters.minRating.toFixed(1)}+</span>
        </div>
        <input
          type="range"
          min={0}
          max={5}
          step={0.1}
          value={filters.minRating}
          onChange={(e) => setFilters({ minRating: parseFloat(e.target.value) })}
          className="w-full accent-indigo-600"
        />
      </div>

      {/* Available for hire */}
      <div>
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Available for hire
          </span>
          <button
            type="button"
            onClick={() => setFilters({ onlyAvailable: !filters.onlyAvailable })}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              filters.onlyAvailable ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-600"
            }`}
            aria-pressed={filters.onlyAvailable}
            aria-label="Toggle available for hire"
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                filters.onlyAvailable ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
        </label>
      </div>

      {/* Max hourly rate */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Max hourly rate
          </h4>
          <span className="text-xs text-gray-500">${currentMaxRate}</span>
        </div>
        <input
          type="range"
          min={0}
          max={500}
          step={5}
          value={currentMaxRate}
          onChange={(e) => setFilters({ maxHourlyRate: parseInt(e.target.value, 10) })}
          className="w-full accent-indigo-600"
        />
        <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.hideMentorsWithoutRate}
            onChange={(e) => setFilters({ hideMentorsWithoutRate: e.target.checked })}
            className="rounded border-gray-300 dark:border-white/20 text-indigo-600 focus:ring-indigo-500"
          />
          Hide mentors without rate
        </label>
      </div>

      {/* Min reviews */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Minimum reviews
        </h4>
        <input
          type="number"
          min={0}
          value={filters.minReviews}
          onChange={(e) =>
            setFilters({ minReviews: Math.max(0, parseInt(e.target.value || "0", 10)) })
          }
          className="w-full rounded-md border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
    </div>
  );
}
